import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAccountClosures,
  getAccountClosureById,
  getClosureAudit,
  approveAccountClosure,
  rejectAccountClosure,
  supportTicketReopen,
} from "@/services/account-closure.service";

export const useAccountClosures = (
  enabled = true,
  params = { page: 0, size: 10 },
  options = {},
) =>
  useQuery({
    queryKey: [
      "account-closures",
      params.page,
      params.size,
      params.status,
      params.blocked,
      params.direction,
      params.search,
    ],
    queryFn: () => getAccountClosures(params),
    enabled,
    keepPreviousData: true,
    ...options,
  });

// staleTime 0: the detail endpoint re-runs the blocker check on every call, so
// a cached copy can claim "approvable" after the situation has changed.
export const useAccountClosure = (id, options = {}) =>
  useQuery({
    queryKey: ["account-closure", id],
    queryFn: () => getAccountClosureById(id),
    enabled: Boolean(id),
    staleTime: 0,
    select: (res) => res?.data ?? res,
    ...options,
  });

export const useClosureAudit = (params = {}, options = {}) =>
  useQuery({
    queryKey: ["closure-audit", params.accountUserId, params.adminId],
    queryFn: () => getClosureAudit(params),
    enabled: Boolean(params.accountUserId || params.adminId),
    select: (res) => res?.data ?? res ?? [],
    ...options,
  });

// Every decision invalidates the queue, this request, and its audit trail —
// a 409 "already decided" also needs the refetch, so invalidation happens on
// settle rather than only on success.
const useDecisionMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: (_data, _err, variables) => {
      const id = variables?.id ?? variables;
      queryClient.invalidateQueries({ queryKey: ["account-closures"] });
      queryClient.invalidateQueries({ queryKey: ["account-closure", id] });
      queryClient.invalidateQueries({ queryKey: ["closure-audit"] });
    },
  });
};

export const useApproveClosure = () =>
  useDecisionMutation(({ id }) => approveAccountClosure(id));

export const useRejectClosure = () => useDecisionMutation(rejectAccountClosure);

export const useSupportTicketReopen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: supportTicketReopen,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["account-closures"] });
      queryClient.invalidateQueries({ queryKey: ["account-closure"] });
    },
  });
};